import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ZodError } from "zod";
import { US_STATES } from "@/lib/us-states";
import { templateSchema, type TemplateValues } from "./schemas";

interface Props {
  values: TemplateValues;
  onChange: (next: TemplateValues) => void;
  errors: Partial<Record<keyof TemplateValues, string>>;
}

const STANDARDS = [
  { value: "e1527-21", label: "ASTM E1527-21 (Phase I ESA)" },
  { value: "e1903-19", label: "ASTM E1903-19 (Phase II ESA)" },
  { value: "aai", label: "EPA All Appropriate Inquiries (40 CFR 312)" },
] as const;

export default function StepTemplate({ values, onChange, errors }: Props) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        These choices pre-fill every new report so your team starts every job
        with consistent defaults.
      </p>
      <div className="space-y-1.5">
        <Label>Default standard</Label>
        <Select
          value={values.standard}
          onValueChange={(v) =>
            onChange({ ...values, standard: v as TemplateValues["standard"] })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STANDARDS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="setup-jurisdiction">Primary jurisdiction (state)</Label>
        <Select
          value={values.jurisdictionState || "none"}
          onValueChange={(v) =>
            onChange({ ...values, jurisdictionState: v === "none" ? "" : v })
          }
        >
          <SelectTrigger id="setup-jurisdiction">
            <SelectValue placeholder="Select a state" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="none">No default</SelectItem>
            {US_STATES.map((s) => (
              <SelectItem key={s.code} value={s.code}>
                {s.code} — {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Used to auto-detect DEQ jurisdictions in the report wizard.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="setup-reqs">Standard regulatory boilerplate</Label>
        <Textarea
          id="setup-reqs"
          rows={3}
          maxLength={500}
          value={values.additionalRequirements ?? ""}
          onChange={(e) =>
            onChange({ ...values, additionalRequirements: e.target.value })
          }
          placeholder="e.g. Include vapor-intrusion screening per state guidance…"
          aria-invalid={!!errors.additionalRequirements}
        />
        {errors.additionalRequirements && (
          <p className="text-xs text-destructive">
            {errors.additionalRequirements}
          </p>
        )}
        <p className="text-xs text-muted-foreground text-right">
          {(values.additionalRequirements ?? "").length}/500
        </p>
      </div>
    </div>
  );
}

export function validateTemplate(v: TemplateValues) {
  try {
    templateSchema.parse(v);
    return { ok: true as const, errors: {} };
  } catch (e) {
    if (e instanceof ZodError) {
      const errors: Partial<Record<keyof TemplateValues, string>> = {};
      for (const issue of e.issues) {
        const k = issue.path[0] as keyof TemplateValues;
        if (!errors[k]) errors[k] = issue.message;
      }
      return { ok: false as const, errors };
    }
    return { ok: false as const, errors: {} };
  }
}
